import React, { useEffect, useMemo, useState } from 'react';
import { Box, Text, Flex, Switch, useBreakpointValue, useMediaQuery, VStack } from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Customized,
} from 'recharts';
import { 
  IUrineLevelDistributionByTimeItem 
} from '../../../interfaces/Analysis/IAnalysis';
import {
  UrineLevelColors, 
  UrineLevelType, 
  getLocalizedUrineLevelLabel 
} from '../../../constants/UrineLevelLabels';
import AleaTrocknenIcon from '../../../icons/AleaTrocknenIcon';
import AleaWenigIcon from '../../../icons/AleaWenigIcon';
import AleaMittelIcon from '../../../icons/AleaMittelIcon';
import AleaVielIcon from '../../../icons/AleaVielIcon';
import AleaVollIcon from '../../../icons/AleaVollIcon';
import { useTranslation } from 'react-i18next';
import {
  useAnalysisUrineLevelDistributionByTimeQuery,
  useAnalysisUrineLevelDistributionQuery,
} from '../analysisPage.queries.ts';
import AleaDownloadButton, {
  type DownloadOption,
} from '../../../components/AleaDownloadButton/AleaDownloadButton';
import { useAuth } from '../../../context/AuthContext';
import { useUrinationPatternExportMutation } from '../analysisPage.mutations';

interface FilterState {
  facilityId?: string;
  facilityName?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
  timestamp?: number;
}

interface AnalysisChartsProps {
  filterState?: FilterState;
  onLoadingChange?: (isLoading: boolean) => void;
  excludeCatheterOverall?: boolean;
  excludeCatheterByTime?: boolean;
  onExcludeCatheterOverallChange?: (exclude: boolean) => void;
  onExcludeCatheterByTimeChange?: (exclude: boolean) => void;
}

// Define colors for stacked chart - Using centralized colors now
const colors = UrineLevelColors;

// Helper function to ensure we have a valid UrineLevelType
const ensureValidUrineLevelType = (label: string): UrineLevelType => {
  if (!label) {
    console.error('Invalid label:', label);
    return UrineLevelType.URINE_MEDIUM; // Default fallback
  }
  
  // Check if the label is already a valid UrineLevelType
  if (Object.values(UrineLevelType).includes(label as UrineLevelType)) {
    return label as UrineLevelType;
  }
  
  // Check if the label might be directly one of the enum values
  for (const enumValue of Object.values(UrineLevelType)) {
    if (enumValue.toLowerCase() === label.toLowerCase()) {
      return enumValue as UrineLevelType;
    }
  }
  
  // Fallback: try to map the string to a UrineLevelType
  const typeMapping: {[key: string]: UrineLevelType} = {
    'TROCKEN': UrineLevelType.URINE_ABSENT,
    'WENIG': UrineLevelType.URINE_LITTLE,
    'MITTEL': UrineLevelType.URINE_MEDIUM,
    'VIEL': UrineLevelType.URINE_MUCH,
    'VOLL': UrineLevelType.URINE_FULL,
    // Add English variations
    'DRY': UrineLevelType.URINE_ABSENT,
    'LOW': UrineLevelType.URINE_LITTLE,
    'MEDIUM': UrineLevelType.URINE_MEDIUM,
    'HIGH': UrineLevelType.URINE_MUCH,
    'FULL': UrineLevelType.URINE_FULL
  };
  
  const mappedType = typeMapping[label.toUpperCase()];
  if (mappedType) {
    return mappedType;
  }
  
  // Last resort: try to match against the enum key patterns
  if (label.toUpperCase().includes('ABSENT') || label.toUpperCase().includes('TROCKEN') || label.toUpperCase().includes('DRY')) {
    return UrineLevelType.URINE_ABSENT;
  }
  if (label.toUpperCase().includes('LITTLE') || label.toUpperCase().includes('WENIG') || label.toUpperCase().includes('LOW')) {
    return UrineLevelType.URINE_LITTLE;
  }
  if (label.toUpperCase().includes('MEDIUM') || label.toUpperCase().includes('MITTEL')) {
    return UrineLevelType.URINE_MEDIUM;
  }
  if (label.toUpperCase().includes('MUCH') || label.toUpperCase().includes('VIEL') || label.toUpperCase().includes('HIGH')) {
    return UrineLevelType.URINE_MUCH;
  }
  if (label.toUpperCase().includes('FULL') || label.toUpperCase().includes('VOLL')) {
    return UrineLevelType.URINE_FULL;
  }
  
  console.warn('Could not map label to UrineLevelType:', label);
  return UrineLevelType.URINE_MEDIUM; // Default fallback
};

type XAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string };
};

const CHART_Y_AXIS_LABEL_OFFSET = 8;
const ICP_QUANTITY_LABEL_ALIGN_OFFSET = 5;
const CHART_Y_AXIS_WIDTH = 100;
const ICP_QUANTITY_ROW_MARGIN = 30;
const CHART_MARGIN = {
  top: ICP_QUANTITY_ROW_MARGIN + 22,
  right: 30,
  left: 20,
  bottom: 10,
};

type IcpQuantityOverlayProps = {
  offset?: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  xAxisMap?: Record<
    string,
    {
      scale?: ((value: string) => number) & { bandwidth?: () => number };
    }
  >;
  amounts?: number[];
  categories?: string[];
  label?: string;
};

const IcpQuantityOverlay = ({
  offset,
  xAxisMap,
  amounts = [],
  categories = [],
  label = 'ICP quantity',
}: IcpQuantityOverlayProps) => {
  if (!offset || amounts.length === 0 || categories.length === 0) {
    return null;
  }

  const xAxis = Object.values(xAxisMap ?? {})[0];
  const scale = xAxis?.scale;
  if (!scale) return null;

  const labelX =
    offset.left - CHART_Y_AXIS_LABEL_OFFSET - ICP_QUANTITY_LABEL_ALIGN_OFFSET;
  const labelY = offset.top - ICP_QUANTITY_ROW_MARGIN;
  const bandwidth = scale.bandwidth?.() ?? 0;

  return (
    <g className="icp-quantity-overlay">
      <text
        x={labelX}
        y={labelY}
        textAnchor="end"
        fontSize={15}
        fontWeight={600}
        fill="#263238"
      >
        {label}
      </text>
      {amounts.map((amount, index) => {
        const category = categories[index];
        if (category == null) return null;
        const x = scale(category) + bandwidth / 2;

        return (
          <text
            key={`${category}-${index}`}
            x={x}
            y={labelY}
            textAnchor="middle"
            fontSize={15}
            fontWeight={600}
            fill="#263238"
          >
            {amount}
          </text>
        );
      })}
    </g>
  );
};

// Custom XAxis tick with icons
const CustomXAxisTick = (props: XAxisTickProps) => {
  const { t } = useTranslation();
  const { x, y, payload } = props;
  
  const urineLevelType = ensureValidUrineLevelType(payload?.value ?? '');
  const displayLabel = getLocalizedUrineLevelLabel(urineLevelType, t);
  const iconColor = UrineLevelColors[urineLevelType] || '#2B4956';
  
  // Determine which icon to show based on the report type
  const renderIcon = () => {
    if (urineLevelType === UrineLevelType.URINE_ABSENT) {
      return <AleaTrocknenIcon width="50px" height="50px" />;
    }
    
    if (urineLevelType === UrineLevelType.URINE_LITTLE) {
      return <AleaWenigIcon color={iconColor} width="50px" height="50px" />;
    }
    
    if (urineLevelType === UrineLevelType.URINE_MEDIUM) {
      return <AleaMittelIcon color={iconColor} width="50px" height="50px" />;
    }
    
    if (urineLevelType === UrineLevelType.URINE_MUCH) {
      return <AleaVielIcon color={iconColor} width="50px" height="50px" />;
    }
    
    if (urineLevelType === UrineLevelType.URINE_FULL) {
      return <AleaVollIcon color={iconColor} width="50px" height="50px" />;
    }
    
    // For other types, use the default drop icon for now
    return (
      <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 21C16.4183 21 20 17.4183 20 13C20 8.58172 12 3 12 3C12 3 4 8.58172 4 13C4 17.4183 7.58172 21 12 21Z" fill={iconColor} />
      </svg>
    );
  };
  
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <foreignObject x="-25" y="0" width="50" height="70">
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          {renderIcon()}
        </div>
      </foreignObject>
      <text x={0} y={75} textAnchor="middle" fill="#666">{displayLabel}</text>
    </g>
  );
};

// Legend circle component 
const LegendCircle = ({ color }: { color: string }) => {
  return (
    <Box 
      width="15px" 
      height="15px" 
      borderRadius="50%" 
      backgroundColor={color} 
    />
  );
};

const AnalysisCharts: React.FC<AnalysisChartsProps> = ({ 
  filterState = {},
  onLoadingChange,
  excludeCatheterOverall = false,
  excludeCatheterByTime = false,
  onExcludeCatheterOverallChange,
  onExcludeCatheterByTimeChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const urinationExportMutation = useUrinationPatternExportMutation();

  const carehomeId = filterState.facilityId ?? user?.carehome?.id;
  const isUrinationExporting = urinationExportMutation.isPending;
  // Use direct media query for more reliable responsive behavior
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  
  // Responsive padding based on screen size - First chart
  const chartPadding = useBreakpointValue({
    base: "10px",   // Very small screens
    sm: "25px",     // > 480px
    md: "25px",     // > 768px
    lg: "25px",     // > 992px
    xl: "50px",     // > 1280px
    "2xl": "50px", // > 1400px (custom breakpoint)
  }) || "25px"; // Fallback value if undefined
  
  // Responsive padding for second chart - less padding even on desktop
  const secondChartPadding = useBreakpointValue({
    base: "10px",   // Very small screens
    sm: "25px",     // > 480px
    md: "25px",     // > 768px
    lg: "25px",     // > 992px
    xl: "50px",     // > 1280px
    "2xl": "50px",  // > 1400px (custom breakpoint)
  }) || "25px"; // Fallback value if undefined
  
  // Use state to manage layout for better handling of refresh
  const [isDesktop, setIsDesktop] = useState(true);

  const includeCatheterLabel = t('includeCatheterResidentData', {
    defaultValue: 'Daten von Bewohnern mit Katheter einschließen.',
  });
  const handleIncludeCatheterOverallChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextInclude = event.target.checked;
    onExcludeCatheterOverallChange?.(!nextInclude);
  };
  const handleIncludeCatheterByTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextInclude = event.target.checked;
    onExcludeCatheterByTimeChange?.(!nextInclude);
  };
  
  // Update layout on media query change and initial render
  useEffect(() => {
    setIsDesktop(isLargerThan992);
  }, [isLargerThan992]);
  
  // Parse the dates from the filter state and return ISO string
  const getDateIsoString = (dateStr?: string, isEndDate = false): string => {
    if (!dateStr) {
      // Use dynamic default dates instead of static ones
      const today = new Date();
      
      if (isEndDate) {
        // Default end date is today
        today.setHours(23, 59, 59, 999);
        return today.toISOString();
      } else {
        // Default start date is 7 days ago
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        // Create UTC date to avoid timezone issues
        return new Date(Date.UTC(
          weekAgo.getFullYear(),
          weekAgo.getMonth(),
          weekAgo.getDate(),
          0, 0, 0
        )).toISOString();
      }
    }
    
    const parts = dateStr.split('.');
    if (parts.length !== 3) {
      // Use dynamic default dates if the format is invalid
      const today = new Date();
      
      if (isEndDate) {
        // Default end date is today
        today.setHours(23, 59, 59, 999);
        return today.toISOString();
      } else {
        // Default start date is 7 days ago
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        // Create UTC date to avoid timezone issues
        return new Date(Date.UTC(
          weekAgo.getFullYear(),
          weekAgo.getMonth(),
          weekAgo.getDate(),
          0, 0, 0
        )).toISOString();
      }
    }
    
    const [day, month, year] = parts;
    
    // For start date, we need to ensure the exact date is used regardless of timezone
    if (!isEndDate) {
      // Create UTC date directly with the same date parts to avoid timezone shift
      const utcDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0));
      const utcIsoString = utcDate.toISOString();
      return utcIsoString;
    }
    
    // For end date, create UTC date directly with 23:59:59.999 to ensure it's 23:59:59.999Z
    // Note: Month in JS Date constructor is 0-indexed
    const utcDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 23, 59, 59, 999));
    return utcDate.toISOString();
  };
  
  const startDateIso = useMemo(
    () => getDateIsoString(filterState.startDate),
    [filterState.startDate]
  );
  const endDateIso = useMemo(
    () => getDateIsoString(filterState.endDate, true),
    [filterState.endDate]
  );

  const canExportUrinationPattern =
    !isUrinationExporting &&
    !!carehomeId &&
    carehomeId !== '1' &&
    (filterState.residentIds?.length ?? 0) > 0;

  const handleUrinationPatternExport = (format: 'pdf' | 'xlsx') => {
    if (!canExportUrinationPattern) return;
    urinationExportMutation.mutate({
      carehomeId: carehomeId!,
      format,
      residentIds: filterState.residentIds!,
    });
  };

  const urinationPatternDownloadOptions: DownloadOption[] = [
    {
      id: 'urination-pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: () => handleUrinationPatternExport('pdf'),
      disabled: !canExportUrinationPattern,
    },
    {
      id: 'urination-xlsx',
      label: 'Export as Excel',
      translationKey: 'general.exportAsExcel',
      onClick: () => handleUrinationPatternExport('xlsx'),
      disabled: !canExportUrinationPattern,
    },
  ];

  const urineLevelDistributionQuery = useAnalysisUrineLevelDistributionQuery({
    timestamp: filterState.timestamp,
    careHomeId: filterState.facilityId,
    startDateIso,
    endDateIso,
    stationIds: filterState.stationIds,
    residentIds: filterState.residentIds,
    excludeCatheter: excludeCatheterOverall,
  });

  const urineLevelByTimeDistributionQuery =
    useAnalysisUrineLevelDistributionByTimeQuery({
      timestamp: filterState.timestamp,
      careHomeId: filterState.facilityId,
      startDateIso,
      endDateIso,
      stationIds: filterState.stationIds,
      residentIds: filterState.residentIds,
      excludeCatheter: excludeCatheterByTime,
    });
  
  // Process API data for the first chart
  const processedUrineLevelData = (urineLevelDistributionQuery.data ?? []).map(item => {
    // Ensure percentage is positive but keep original decimal precision
    const percentageValue = Math.abs(parseFloat(item.percentage.toString()));
    
    // Try to map the report_label to a valid UrineLevelType
    const labelType = ensureValidUrineLevelType(item.report_label);
    
    return {
      name: labelType, // Use the UrineLevelType enum value directly
      originalLabel: item.report_label, // Keep original for reference
      value: percentageValue,
      amount: item.amount ?? 0,
      color: UrineLevelColors[labelType] || '#2B4956'
    };
  });

  // Process API data for the second chart - transform time-based data with minimum height
  type TimeRow = Record<string, string | number>;
  const processTimeData = (
    timeItems: IUrineLevelDistributionByTimeItem[]
  ): TimeRow[] => {
    if (timeItems.length === 0) return [];

    const timeBlocks = [...new Set(timeItems.map((item) => item.time_block))];
    const reportLabels = [...new Set(timeItems.map((item) => item.report_label))];

    const dataToProcess: TimeRow[] = timeBlocks.map((timeBlock) => {
      const timeData: TimeRow = { time: timeBlock };
      let totalAmount = 0;

      for (const label of reportLabels) {
        const entry = timeItems.find(
          (data) => data.time_block === timeBlock && data.report_label === label
        );
        const percentageValue = entry
          ? Math.abs(parseFloat(entry.percentage.toString()))
          : 0;
        timeData[label] = percentageValue;

        if (entry) {
          totalAmount += entry.amount ?? 0;
        }
      }

      timeData.totalAmount = totalAmount;

      return timeData;
    });

    return dataToProcess.map((item) => {
      const result: TimeRow = { ...item };

      for (const key of Object.keys(result)) {
        if (
          key !== 'time' &&
          key !== 'totalAmount' &&
          typeof result[key] === 'number'
        ) {
          result[`${key}_original`] = result[key];
        }
      }

      for (const key of Object.keys(result)) {
        const value = result[key];
        if (
          key !== 'time' &&
          key !== 'totalAmount' &&
          !key.endsWith('_original') &&
          typeof value === 'number' &&
          value < 7 &&
          value > 0
        ) {
          result[key] = 7;
        }
      }

      return result;
    });
  };
  
  // Get processed time data
  const timeDataItems = urineLevelByTimeDistributionQuery.data ?? [];
  const processedTimeData =
    !urineLevelByTimeDistributionQuery.isFetching && timeDataItems.length > 0
      ? processTimeData(timeDataItems)
      : [];
    
  // Dynamic colors based on reportLabels
  const getDynamicColors = () => {
    if (timeDataItems.length === 0) {
      return colors; // Return default colors if no data
    }
    
    // Extract unique report labels
    const reportLabels = [...new Set(timeDataItems.map(item => item.report_label))];
    
    // Create dynamic color object
    const dynamicColors: { [key: string]: string } = {};
    reportLabels.forEach(label => {
      dynamicColors[label] = UrineLevelColors[label as UrineLevelType] || '#2B4956'; // Fallback color
    });
    
    return dynamicColors;
  };
  
  // Get dynamic colors
  const dynamicColors = getDynamicColors();

  useEffect(() => {
    onLoadingChange?.(
      urineLevelDistributionQuery.isFetching ||
        urineLevelByTimeDistributionQuery.isFetching
    );
  }, [
    onLoadingChange,
    urineLevelByTimeDistributionQuery.isFetching,
    urineLevelDistributionQuery.isFetching,
  ]);

  return (
    <VStack align="stretch" spacing="100px" w="100%">
      {/* First Chart - Overall IKP Load */}
      <Box data-chart="first">
        <Flex
          direction={isDesktop ? 'row' : 'column'}
          justify="space-between"
          align={isDesktop ? 'flex-start' : 'flex-start'}
          gap="20px"
          mb="40px"
        >
          <Flex align="center" gap="12px">
            {/* TODO: Temporarily hidden - re-enable when ready */}
            {false && (
              <Box>
                <AleaDownloadButton options={urinationPatternDownloadOptions} dropdownAlign="right" />
              </Box>
            )}
            <Box>
              <Text fontSize="25px" fontWeight="500" color="black" mb="0">
                {t('analyse.ikpUtilizationAtChangeTime')}
              </Text>
              <Text fontSize="18px" fontWeight="normal" color="black" mb="0">
                {t('analyse.gesamt')}
              </Text>
            </Box>
          </Flex>
          <Flex
            align="center"
            gap="12px"
            mt={isDesktop ? '8px' : '0'}
            maxW={isDesktop ? '260px' : '100%'}
          >
            <Switch
              colorScheme="green"
              isChecked={!excludeCatheterOverall}
              onChange={handleIncludeCatheterOverallChange}
            />
            <Text fontSize="15px" fontWeight="300" color="#000" lineHeight="1.2">
              {includeCatheterLabel}
            </Text>
          </Flex>
        </Flex>

        <Box maxW="800px" mx="auto" px={chartPadding} data-chart-content="first">
          <Box height="350px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={processedUrineLevelData}
                margin={CHART_MARGIN}
                barSize={75}
                style={{ overflow: 'visible' }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                  height={100}
                  interval={0}
                  tick={<CustomXAxisTick />}
                />
                <YAxis
                  width={CHART_Y_AXIS_WIDTH}
                  axisLine={false}
                  tickLine={false}
                  tick={{ dx: -CHART_Y_AXIS_LABEL_OFFSET }}
                  tickFormatter={(value) => `${value}%`}
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                />
                <Customized
                  component={IcpQuantityOverlay}
                  amounts={processedUrineLevelData.map((item) => item.amount)}
                  categories={processedUrineLevelData.map((item) => item.name)}
                  label={t('icpQuantity', { defaultValue: 'ICP quantity' })}
                />
                <Bar dataKey="value" radius={[0, 0, 0, 0]} fill="#2B4956">
                  <LabelList
                    dataKey="value"
                    position="top"
                    formatter={(value: number) => `${value.toFixed(2)}%`}
                    style={{ fontWeight: 'bold', fill: '#2B4956' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      {/* Second Chart - Time-based IKP Load */}
      <Box data-chart="second">
        <Flex
          direction={isDesktop ? 'row' : 'column'}
          justify="space-between"
          align={isDesktop ? 'flex-start' : 'flex-start'}
          gap="20px"
          mb="40px"
        >
          <Box>
            <Text fontSize="25px" fontWeight="500" color="black" mb="0">
              {t('analyse.ikpUtilizationAtChangeTime')}
            </Text>
            <Text fontSize="18px" fontWeight="normal" color="black" mb="0">
              {t('analyse.perTimeOfDay')}
            </Text>
          </Box>
          <Flex
            align="center"
            gap="12px"
            mt={isDesktop ? '8px' : '0'}
            maxW={isDesktop ? '260px' : '100%'}
          >
            <Switch
              colorScheme="green"
              isChecked={!excludeCatheterByTime}
              onChange={handleIncludeCatheterByTimeChange}
            />
            <Text fontSize="15px" fontWeight="300" color="#000" lineHeight="1.2">
              {includeCatheterLabel}
            </Text>
          </Flex>
        </Flex>

        <Box px={secondChartPadding} maxW="910px" mx="auto" data-chart-content="second">
          <Flex
            direction={isDesktop ? 'row' : 'column'}
            align={isDesktop ? 'center' : 'flex-start'}
          >
            {/* Legend - Left side on desktop, top on mobile but still vertical */}
            <Flex
              direction="column"
              mr={isDesktop ? '30px' : '0'}
              mb={isDesktop ? '0' : '20px'}
              mt={isDesktop ? '0' : '0'}
              justify="center"
              width={isDesktop ? 'auto' : 'auto'}
              alignSelf={isDesktop ? 'center' : 'flex-start'}
              className="pdf-legend-container"
            >
              {Object.entries(dynamicColors)
                .reverse()
                .map(([key, color], index) => {
                  const displayLabel = getLocalizedUrineLevelLabel(key as UrineLevelType, t);
                  return (
                    <Flex key={index} alignItems="center" mr="0" mb="15px">
                      <LegendCircle color={color} />
                      <Text ml="8px" fontSize="14px" className="pdf-legend-label">
                        {displayLabel}
                      </Text>
                    </Flex>
                  );
                })}
            </Flex>

            {/* Chart */}
            <Box flex="1" width={isDesktop ? 'auto' : '100%'}>
              <Box height="350px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      urineLevelByTimeDistributionQuery.isFetching
                        ? []
                        : processedTimeData
                    }
                    margin={CHART_MARGIN}
                    stackOffset="expand"
                    barSize={75}
                    style={{ overflow: 'visible' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="time"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 14 }}
                      interval={0}
                    />
                    <YAxis
                      width={CHART_Y_AXIS_WIDTH}
                      axisLine={false}
                      tickLine={false}
                      tick={{ dx: -CHART_Y_AXIS_LABEL_OFFSET }}
                      tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      domain={[0, 1]}
                      ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                    />
                    <Customized
                      component={IcpQuantityOverlay}
                      amounts={processedTimeData.map((item) =>
                        typeof item.totalAmount === 'number' ? item.totalAmount : 0
                      )}
                      categories={processedTimeData.map((item) => String(item.time))}
                      label={t('icpQuantity', { defaultValue: 'ICP quantity' })}
                    />
                    {/* Dynamically generate bars based on report labels */}
                    {Object.entries(dynamicColors).map(([key, color]) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={color}
                        name={key}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey={`${key}_original`}
                          position="center"
                          formatter={(value: number) =>
                            value > 0 ? `${value.toFixed(2)}%` : ''
                          }
                          style={{
                            fontWeight: 'bold',
                            fill: '#000',
                            fontSize: '13px',
                          }}
                        />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Flex>
        </Box>
      </Box>
    </VStack>
  );
};

export default AnalysisCharts;
